/**
 * The raw layer of the SYD-2493 lake: the column families a raw DuckLake table is made of,
 * and the row shape ingestion writes. Consumers are the change-feed drain and the
 * snapshot-replace job that land Firestore documents here, the reconciliation view that
 * overlays one channel on the other, and the fact build that reads roles out again.
 *
 * `Meta` ends in an open index signature and `ArRowData.cells` is keyed by
 * deployment-defined TypeIds, so no wide typed table can exist. The resolution is that only
 * the spine and the bound role columns are ever typed; everything else stays verbatim in
 * `payload`, where promoting a key to a typed column later is a configuration change and a
 * re-ingest rather than a migration. A value that does not match its declared type is
 * written null and quarantined — coercion is how a warehouse starts lying.
 *
 * Reconciliation orders by source time, not arrival: a feed row overlays a snapshot row only
 * when its `eventTime` is after that row's `observedAt`, the Firestore read time of the page
 * that scanned it, so the snapshot wins an exact tie. Among feed rows for one document the
 * latest `eventTime` wins, with `changeSeq` breaking only exact ties, and a winning tombstone
 * (`deletedAt` set) hides the document.
 */

import type { DocId } from './System.types';
import type { ColumnRoleId, RawTableId } from './Analytics.Catalog.types';

/**
 * The DuckDB type a typed column may take. `'DATE'` is for calendar bounds such as a goal's
 * period and for nothing else: an event carries an instant, and storing one as a local date
 * is what moves it across a bucket edge when the reporting zone changes.
 */
export type RawColumnType = 'DECIMAL(18,4)' | 'TIMESTAMPTZ' | 'DATE' | 'VARCHAR' | 'BIGINT' | 'BOOLEAN';

/**
 * Which ingestion channel wrote the row. A snapshot row's effective time is its `observedAt`
 * and a feed row's is its `eventTime`; a feed row overlays a snapshot row only when strictly
 * newer.
 */
export type RawSourceChannel = 'snapshot' | 'feed';

/**
 * Column family one: fixed, typed and identical on every raw table.
 *
 * `sourceModifiedAt` is the source document's own field and is not a change marker — the
 * derive engine writes cells without moving it, so a reader watching it misses every
 * machine-authored change. `observedAt` is the Firestore read time of the page that observed
 * the document, and is the trustworthy one. `createdAt` and `sourceModifiedAt` are null when
 * the source held anything but a resolved `Timestamp`.
 *
 * `eventTime` is the commit time the change feed recorded for the source change, in UTC
 * microseconds since the epoch as DuckDB parsed the outbox's text, a bigint because a `Date` would
 * drop the microseconds. `changeSeq` breaks exact `eventTime` ties. `deletedAt` is a delete's
 * `eventTime`, and null while the document is live. All three are null on a snapshot row.
 */
export type RawSpine = {
  docPath: string;
  collectionId: RawTableId;
  parentPath: string;
  docId: DocId;
  createdAt: Date | null;
  sourceModifiedAt: Date | null;
  observedAt: Date;
  eventTime: bigint | null;
  deletedAt: bigint | null;
  generation: number;
  changeSeq: number | null;
  sourceChannel: RawSourceChannel;
};

/** The DDL for the spine. Every timestamp is a UTC instant; no formatted date reaches storage. */
export const RAW_SPINE_COLUMNS = {
  docPath: 'VARCHAR',
  collectionId: 'VARCHAR',
  parentPath: 'VARCHAR',
  docId: 'VARCHAR',
  createdAt: 'TIMESTAMPTZ',
  sourceModifiedAt: 'TIMESTAMPTZ',
  observedAt: 'TIMESTAMPTZ',
  eventTime: 'TIMESTAMPTZ',
  deletedAt: 'TIMESTAMPTZ',
  generation: 'BIGINT',
  changeSeq: 'BIGINT',
  sourceChannel: 'VARCHAR',
} as const satisfies Record<keyof RawSpine, RawColumnType>;

/**
 * Column family two: the storage type of every role the catalog can read. The type belongs to
 * the role rather than to the table it lands on, so two tables carrying the same role cannot
 * disagree about it. Which roles a given table carries is the ingestion binding's business
 * and lives with the service; what a role means is code, and lives here because the binding
 * UI validates a candidate field against it.
 */
export const ROLE_COLUMN_TYPE = {
  salespersonMemberId: 'VARCHAR',
  officeMemberId: 'VARCHAR',
  territoryMemberId: 'VARCHAR',
  manufacturerMemberId: 'VARCHAR',
  customerMemberId: 'VARCHAR',
  salesCreditAmount: 'DECIMAL(18,4)',
  salesCreditDate: 'TIMESTAMPTZ',
  quoteAmount: 'DECIMAL(18,4)',
  quoteDate: 'TIMESTAMPTZ',
  quoteHasSpecifier: 'BOOLEAN',
  orderAmount: 'DECIMAL(18,4)',
  orderDate: 'TIMESTAMPTZ',
  lineItemStatus: 'VARCHAR',
  lineItemKind: 'VARCHAR',
  lineItemType: 'VARCHAR',
  lineItemDescription: 'VARCHAR',
  manufacturerName: 'VARCHAR',
  lineItemReleaseDate: 'TIMESTAMPTZ',
  lineItemPromisedDate: 'TIMESTAMPTZ',
  lineItemQuantity: 'BIGINT',
  lineItemShipped: 'BOOLEAN',
  lineItemShipDate: 'TIMESTAMPTZ',
  invoiceOutstandingAmount: 'DECIMAL(18,4)',
  invoiceDate: 'TIMESTAMPTZ',
  invoiceNumber: 'VARCHAR',
  invoiceStatus: 'VARCHAR',
  invoiceDueDate: 'TIMESTAMPTZ',
  invoiceAmount: 'DECIMAL(18,4)',
  goalTarget: 'DECIMAL(18,4)',
  goalPeriodStart: 'DATE',
  goalPeriodEnd: 'DATE',
} as const satisfies Record<ColumnRoleId, RawColumnType>;

/** What each storage type reads back as. `'DATE'` is an ISO calendar date, never an instant. */
type RawColumnValue = {
  'DECIMAL(18,4)': number;
  TIMESTAMPTZ: Date;
  DATE: string;
  VARCHAR: string;
  BIGINT: number;
  BOOLEAN: boolean;
};

/**
 * Column family two, populated. A role the table does not carry is absent; a role whose
 * source value failed its declared type is null, which is how quarantine reads downstream.
 */
export type RawRoleValues = {
  [Role in ColumnRoleId]?: RawColumnValue[(typeof ROLE_COLUMN_TYPE)[Role]] | null;
};

/**
 * One Firestore document as ingestion writes it. The row is flat, matching the table the
 * appender writes, so no per-row rebuild stands between extraction and the write. Column
 * family three is `payload`: the source document verbatim as JSON text, which DuckDB queries
 * natively, so an unbound field stays analyzable without re-ingesting history. It is encoded
 * once, at binding, so its length is what a batch holds in memory and nothing re-serialises it.
 */
export type RawRow = RawSpine & RawRoleValues & { payload: string };
